<template>
  <div class="file-viewer-crop h-full w-full">
    <div
      ref="workspaceRef"
      class="relative flex h-full w-full flex-col items-center justify-center"
    >
      <!-- Instructions -->
      <div v-if="!hasFourPoints && !isProcessing" class="surface mb-2 rounded-md border p-2">
        <p class="space-x-2 text-sm font-medium select-none">
          <span>Klik op de vier hoeken om bij te snijden</span>
          <span class="text-secondary">{{ clickCount }}/4</span>
          <PButton :icon="['fas', 'xmark']" size="sm" @click="emit('close')"> Annuleren </PButton>
        </p>
      </div>

      <div
        ref="canvasContainer"
        class="relative touch-none select-none"
        :class="!hasFourPoints ? 'cursor-crosshair' : ''"
        :style="
          displayWidth > 0 ? { width: displayWidth + 'px', height: displayHeight + 'px' } : {}
        "
        @pointerdown.prevent="handleClick"
      >
        <img
          ref="imageRef"
          :src="imageUrl"
          class="block max-h-full max-w-full rounded-md object-contain"
          alt="Crop Source"
          draggable="false"
          crossorigin="anonymous"
          @load="onImageLoad"
        />

        <!-- SVG Overlay -->
        <svg
          class="pointer-events-none absolute inset-0 z-10 h-full w-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Temporary path showing clicked points -->
          <path
            v-if="tempPath && !hasFourPoints"
            :d="tempPath"
            stroke-width="2"
            stroke-dasharray="4"
            fill="none"
            class="stroke-secondary"
          />
          <!-- Complete polygon when all 4 points are set -->
          <polygon
            v-if="polygonPoints"
            :points="polygonPoints"
            stroke-width="2"
            stroke-dasharray="4"
            class="fill-secondary/15 stroke-secondary"
          />
        </svg>

        <!-- Raw clicked points (before sorting) -->
        <div
          v-for="(point, idx) in rawPoints"
          :key="'raw-' + idx"
          v-show="!hasFourPoints"
          class="secondary border-surface absolute z-20 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
          :style="{ left: point.x + 'px', top: point.y + 'px' }"
        />

        <!-- Sorted corner handles (after 4 points, for adjustment) -->
        <template v-if="hasFourPoints">
          <div
            v-for="(corner, key) in corners"
            :key="key"
            v-show="corner"
            class="secondary border-surface absolute z-20 size-5 -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 transition-transform duration-100"
            :style="{ left: corner?.x + 'px', top: corner?.y + 'px' }"
            @pointerdown.stop="startDrag(key as CornerKey, $event)"
          />
        </template>
      </div>

      <!-- Processing indicator -->
      <div
        v-if="isProcessing"
        class="absolute inset-0 z-40 flex items-center justify-center rounded-md backdrop-blur-xs backdrop-brightness-50"
      >
        <div class="surface flex items-center space-x-2 rounded-md p-2">
          <PProgress as="span" size="sm" aria-label="Afbeelding wordt verwerkt" class="w-24!" />
          <span class="font-medium">Verwerken...</span>
        </div>
      </div>

      <!-- OpenCV loading indicator -->
      <div
        v-if="!isOpenCvReady"
        class="absolute inset-0 z-40 flex items-center justify-center rounded-md backdrop-blur-xs backdrop-brightness-50"
      >
        <div class="surface flex items-center space-x-2 rounded-md p-2">
          <PProgress
            as="span"
            size="sm"
            aria-label="Afbeeldingsbewerking wordt geladen"
            class="w-24!"
          />
        </div>
      </div>
    </div>

    <!-- Hidden Canvas for OpenCV processing -->
    <canvas ref="outputCanvas" class="hidden" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue"
import PButton from "@/components/elements/PButton.vue"
import PProgress from "@/components/elements/PProgress.vue"

// OpenCV type declaration
declare global {
  interface Window {
    cv: any
  }
}

type CornerKey = "tl" | "tr" | "br" | "bl"

interface Point {
  x: number
  y: number
}

interface Corners {
  tl: Point | null
  tr: Point | null
  br: Point | null
  bl: Point | null
}

const OPENCV_CDN = "https://docs.opencv.org/5.0.0/opencv.js"
const OPENCV_LOCAL = "/js/opencv.js"

defineProps<{
  imageUrl: string
}>()

const emit = defineEmits<{
  close: []
  complete: [blob: Blob]
}>()

// Refs
const workspaceRef = ref<HTMLDivElement | null>(null)
const canvasContainer = ref<HTMLDivElement | null>(null)
const imageRef = ref<HTMLImageElement | null>(null)
const outputCanvas = ref<HTMLCanvasElement | null>(null)

// State
const isOpenCvReady = ref(false)
const isProcessing = ref(false)

// Dimensions
const displayWidth = ref(0)
const displayHeight = ref(0)
const naturalWidth = ref(0)
const naturalHeight = ref(0)

// Points
const rawPoints = ref<Point[]>([])
const corners = ref<Corners>({ tl: null, tr: null, br: null, bl: null })
const hasFourPoints = ref(false)

// Drag state
const activeDragHandle = ref<CornerKey | null>(null)

// Auto-crop timer
let autoCropTimer: ReturnType<typeof setTimeout> | null = null

// Computed
const clickCount = computed(() => rawPoints.value.length)

const polygonPoints = computed(() => {
  const c = corners.value
  if (!hasFourPoints.value || !c.tl || !c.tr || !c.br || !c.bl) return ""
  return `${c.tl.x},${c.tl.y} ${c.tr.x},${c.tr.y} ${c.br.x},${c.br.y} ${c.bl.x},${c.bl.y}`
})

const tempPath = computed(() => {
  const pts = rawPoints.value
  if (pts.length === 0) return ""
  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 1; i < pts.length; i++) {
    d += ` L ${pts[i].x} ${pts[i].y}`
  }
  return d
})

function injectScript(src: string): Promise<void> {
  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}

function waitForOpenCv(): Promise<void> {
  if (window.cv?.Mat) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const check = setInterval(() => {
      if (window.cv) {
        clearInterval(check)
        clearTimeout(timeout)
        resolve()
      }
    }, 100)

    const timeout = setTimeout(() => {
      clearInterval(check)
      reject(new Error("OpenCV.js init timeout"))
    }, 30000)
  })
}

// Load OpenCV.js: CDN first, local fallback
async function loadOpenCV(): Promise<void> {
  if (window.cv?.Mat) {
    isOpenCvReady.value = true
    return
  }

  try {
    await injectScript(OPENCV_CDN)
    await waitForOpenCv()
  } catch {
    await injectScript(OPENCV_LOCAL)
    await waitForOpenCv()
  }

  isOpenCvReady.value = true
}

// Initialize image dimensions
function onImageLoad() {
  const img = imageRef.value
  const container = workspaceRef.value

  if (!img || !container) {
    return
  }

  naturalWidth.value = img.naturalWidth
  naturalHeight.value = img.naturalHeight

  const maxWidth = container.clientWidth - 40
  const maxHeight = container.clientHeight - 40
  const ratio = Math.min(maxWidth / naturalWidth.value, maxHeight / naturalHeight.value, 1)

  displayWidth.value = Math.round(naturalWidth.value * ratio)
  displayHeight.value = Math.round(naturalHeight.value * ratio)
}

// Sort 4 points into corners based on position
function getSortedCorners(pts: Point[]): Corners {
  const sorted = [...pts].sort((a, b) => a.y - b.y)
  const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x)
  const bottom = sorted.slice(2, 4).sort((a, b) => a.x - b.x)
  return {
    tl: top[0],
    tr: top[1],
    bl: bottom[0],
    br: bottom[1]
  }
}

// Handle click to add a point
function handleClick(event: PointerEvent) {
  if (hasFourPoints.value) return

  const container = canvasContainer.value
  if (!container) return

  const rect = container.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  rawPoints.value.push({ x, y })

  if (rawPoints.value.length === 4) {
    finishSelection()
  }
}

// Finish selection: sort points and trigger crop
function finishSelection() {
  const sorted = getSortedCorners(rawPoints.value)
  corners.value = sorted
  hasFourPoints.value = true
  triggerAutoCrop()
}

// Trigger auto-crop with delay
function triggerAutoCrop() {
  if (autoCropTimer) clearTimeout(autoCropTimer)
  autoCropTimer = setTimeout(() => {
    processCrop()
  }, 400)
}

// Drag handling
function startDrag(handle: CornerKey, event: PointerEvent) {
  if (!hasFourPoints.value) return
  ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
  activeDragHandle.value = handle
  if (autoCropTimer) {
    clearTimeout(autoCropTimer)
    autoCropTimer = null
  }
}

function onDrag(event: PointerEvent) {
  if (!activeDragHandle.value) return
  event.preventDefault()

  const container = canvasContainer.value
  if (!container) return

  const rect = container.getBoundingClientRect()
  let x = event.clientX - rect.left
  let y = event.clientY - rect.top

  x = Math.max(0, Math.min(x, displayWidth.value))
  y = Math.max(0, Math.min(y, displayHeight.value))

  const corner = corners.value[activeDragHandle.value]
  if (corner) {
    corner.x = x
    corner.y = y
  }
}

function stopDrag() {
  activeDragHandle.value = null
}

// Process crop with OpenCV
async function processCrop() {
  const c = corners.value
  if (!isOpenCvReady.value || !window.cv) return
  if (!c.tl || !c.tr || !c.br || !c.bl) return
  isProcessing.value = true

  await nextTick()
  await new Promise((resolve) => setTimeout(resolve, 100))

  try {
    const cv = window.cv
    const scaleX = naturalWidth.value / displayWidth.value
    const scaleY = naturalHeight.value / displayHeight.value

    const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
      c.tl.x * scaleX,
      c.tl.y * scaleY,
      c.tr.x * scaleX,
      c.tr.y * scaleY,
      c.br.x * scaleX,
      c.br.y * scaleY,
      c.bl.x * scaleX,
      c.bl.y * scaleY
    ])

    const widthTop = Math.hypot((c.tr.x - c.tl.x) * scaleX, (c.tr.y - c.tl.y) * scaleY)
    const widthBottom = Math.hypot((c.br.x - c.bl.x) * scaleX, (c.br.y - c.bl.y) * scaleY)
    const outputWidth = Math.round(Math.max(widthTop, widthBottom))

    const heightLeft = Math.hypot((c.bl.x - c.tl.x) * scaleX, (c.bl.y - c.tl.y) * scaleY)
    const heightRight = Math.hypot((c.br.x - c.tr.x) * scaleX, (c.br.y - c.tr.y) * scaleY)
    const outputHeight = Math.round(Math.max(heightLeft, heightRight))

    const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0,
      0,
      outputWidth,
      0,
      outputWidth,
      outputHeight,
      0,
      outputHeight
    ])

    const tempCanvas = document.createElement("canvas")
    tempCanvas.width = naturalWidth.value
    tempCanvas.height = naturalHeight.value
    const ctx = tempCanvas.getContext("2d")
    if (!ctx || !imageRef.value) throw new Error("Canvas context not available")

    ctx.drawImage(imageRef.value, 0, 0)

    const src = cv.imread(tempCanvas)
    const dst = new cv.Mat()
    const M = cv.getPerspectiveTransform(srcTri, dstTri)
    const dsize = new cv.Size(outputWidth, outputHeight)

    cv.warpPerspective(src, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar())

    if (!outputCanvas.value) throw new Error("Output canvas not available")
    cv.imshow(outputCanvas.value, dst)

    const dataUrl = outputCanvas.value.toDataURL("image/jpeg", 0.95)

    // Cleanup OpenCV matrices
    src.delete()
    dst.delete()
    M.delete()
    srcTri.delete()
    dstTri.delete()
    tempCanvas.width = 0
    tempCanvas.height = 0

    // Convert data URL to blob and emit
    const response = await fetch(dataUrl)
    const blob = await response.blob()

    emit("complete", blob)
  } catch (err) {
    console.error("Crop processing error:", err)
    alert("Fout bij verwerken van de afbeelding.")
  } finally {
    isProcessing.value = false
  }
}

// Event listeners
function setupDragListeners() {
  window.addEventListener("pointermove", onDrag)
  window.addEventListener("pointerup", stopDrag)
}

function cleanupDragListeners() {
  window.removeEventListener("pointermove", onDrag)
  window.removeEventListener("pointerup", stopDrag)
}

onMounted(async () => {
  setupDragListeners()
  try {
    await loadOpenCV()
  } catch (err) {
    console.error("Failed to load OpenCV:", err)
  }
})

onUnmounted(() => {
  cleanupDragListeners()
  if (autoCropTimer) {
    clearTimeout(autoCropTimer)
  }
})
</script>
